import axiosClient from '../interceptors';
import {ApiRoutes} from "../routes/ApiRoutes";
import {getAPIUrl} from "./BaseService";
import {cleanFormPayload, snakeifyObjectKeys} from "./HelperService";
import axios from "axios";
import {AccountTypeValue, ApiResponse, SchoolUser, UserProfilePayload} from "../types";

export interface ImportRow {
    [key: string]: string | undefined;
}

export interface ImportSummary {
    created: number;
    skipped: number;
    errors: { row: number; message: string }[];
    warnings?: { row: number; message: string }[];
}

export interface GetUsersParams {
    sessionId?: string;
    termId?: string;
    skip?: number;
    limit?: number;
    search?: string;
}

export interface PaginatedUsersResponse {
    items: SchoolUser[];
    total: number;
    skip: number;
    limit: number;
}

export interface GuardianStudentLink {
    id: string;
    guardian_id: string;
    student_id: string;
    relationship_type?: string | null;
    created_at: string;
}

export async function getUsers(
    type: AccountTypeValue | null = null,
    params: GetUsersParams = {},
): Promise<PaginatedUsersResponse> {
    const query: Record<string, string> = {
        skip: String(params.skip ?? 0),
        limit: String(params.limit ?? 10),
    };
    if (type) query.role = type;
    if (params.sessionId) query.session_id = params.sessionId;
    if (params.termId) query.term_id = params.termId;
    if (params.search) query.search = params.search;
    const url = getAPIUrl() + ApiRoutes.SCHOOL_USERS;
    const response = await axiosClient.get<PaginatedUsersResponse>(url, { params: query });
    return response.data;
}

export async function addUser(userDetails: Record<string, unknown>, type: AccountTypeValue): Promise<ApiResponse> {
    const url = getAPIUrl() + ApiRoutes.SCHOOL_USER;
    const snaked = snakeifyObjectKeys(cleanFormPayload(userDetails));
    const response = await axiosClient.post<ApiResponse>(url, {...snaked, user_type: type});
    return response.data;
}

export async function addStudent(userDetails: Record<string, unknown>): Promise<ApiResponse> {
    const url = getAPIUrl() + "/users/students";
    const snaked = snakeifyObjectKeys(cleanFormPayload(userDetails));
    const response = await axiosClient.post<ApiResponse>(url, snaked);
    return response.data;
}

export async function addGuardian(userDetails: Record<string, unknown>): Promise<ApiResponse> {
    const url = getAPIUrl() + "/users/guardians";
    const snaked = snakeifyObjectKeys(cleanFormPayload(userDetails));
    const response = await axiosClient.post<ApiResponse>(url, snaked);
    return response.data;
}

export async function inviteUser(userDetails: Record<string, unknown>, schoolId: string): Promise<ApiResponse> {
    const url = getAPIUrl() + ApiRoutes.INVITE_SCHOOL_USER + '?school_id=' + encodeURIComponent(schoolId);
    const snaked = snakeifyObjectKeys(cleanFormPayload(userDetails));
    const response = await axiosClient.post<ApiResponse>(url, {...snaked});
    return response.data;
}

export async function getInvitePreview(token: string): Promise<{ email: string; first_name?: string; school_name?: string }> {
    const url = getAPIUrl() + ApiRoutes.INVITE_PREVIEW;
    const response = await axios.get(url, { params: { token } });
    return response.data;
}

export async function acceptInvite(token: string, password: string, passwordConfirmation: string): Promise<ApiResponse> {
    const url = getAPIUrl() + ApiRoutes.INVITE_ACCEPT;
    const response = await axios.post<ApiResponse>(url, {
        token,
        password,
        password_confirmation: passwordConfirmation,
    });
    return response.data;
}

export async function resendInvite(userId: string): Promise<ApiResponse> {
    const url = getAPIUrl() + ApiRoutes.SEND_INVITE + '/' + userId + '/resend';
    const response = await axiosClient.post<ApiResponse>(url);
    return response.data;
}

export async function importUsers(
    rows: ImportRow[],
    schoolId: string,
    userType: AccountTypeValue,
    sendInvite: boolean = true,
): Promise<ImportSummary> {
    const url = getAPIUrl() + ApiRoutes.IMPORT_USERS_BULK;
    const response = await axiosClient.post<ImportSummary>(url, {
        rows,
        school_id: schoolId,
        user_type: userType,
        send_invite: sendInvite,
    });
    return response.data;
}

export async function editUser(id: string, userDetails: Record<string, unknown>): Promise<ApiResponse> {
    const url = getAPIUrl() + ApiRoutes.SCHOOL_USER + '/' + id;
    const snaked = snakeifyObjectKeys(cleanFormPayload({ ...userDetails, user_uuid: id }));
    const response = await axiosClient.patch<ApiResponse>(url, { ...snaked });
    return response.data;
}

export async function deleteUser(id: string): Promise<ApiResponse> {
    const url = getAPIUrl() + ApiRoutes.SCHOOL_USER + '/' + id;
    const response = await axiosClient.delete<ApiResponse>(url);
    return response.data;
}

export async function validateUserDetails(
    userDetails: Record<string, unknown>,
    type: AccountTypeValue,
    userExists: boolean = false
): Promise<ApiResponse> {
    const url = getAPIUrl() + ApiRoutes.SCHOOL_USER + '/' + type + '/validate' + (userExists ? ('/existing') : '');
    const snaked = snakeifyObjectKeys(userDetails);
    const response = await axiosClient.post<ApiResponse>(url, {...snaked});
    return response.data;
}

export interface GetUserProfileParams {
    termId?: string;
    sessionId?: string;
}

export async function getUserProfile(
    id: string,
    params: GetUserProfileParams = {},
): Promise<UserProfilePayload> {
    const query: Record<string, string> = {};
    if (params.termId) query.term_id = params.termId;
    if (params.sessionId) query.session_id = params.sessionId;
    const url = getAPIUrl() + ApiRoutes.SCHOOL_USER + '/' + id + '/profile';
    const response = await axiosClient.get<UserProfilePayload>(url, { params: query });
    return response.data;
}

export async function getUser(id: string): Promise<SchoolUser> {
    const url = getAPIUrl() + ApiRoutes.SCHOOL_USER + '/' + id;
    const response = await axiosClient.get<SchoolUser>(url);
    return response.data;
}

export async function linkGuardianToStudent(
    studentId: string,
    guardianId: string,
    relationshipType: string = 'parent'
): Promise<GuardianStudentLink> {
    const url = getAPIUrl() + ApiRoutes.SCHOOL_USER + '/' + studentId + '/guardians';
    const response = await axiosClient.post<GuardianStudentLink>(url, {
        guardian_id: guardianId,
        relationship_type: relationshipType,
    });
    return response.data;
}

export async function listGuardiansForStudent(
    studentId: string
): Promise<GuardianStudentLink[]> {
    const url = getAPIUrl() + ApiRoutes.SCHOOL_USER + '/' + studentId + '/guardians';
    const response = await axiosClient.get<GuardianStudentLink[]>(url);
    return response.data;
}

export async function listWardsForGuardian(
    guardianId: string
): Promise<GuardianStudentLink[]> {
    const url = getAPIUrl() + ApiRoutes.SCHOOL_USER + '/' + guardianId + '/wards';
    const response = await axiosClient.get<GuardianStudentLink[]>(url);
    return response.data;
}

export async function updateGuardianStudentLink(
    studentId: string,
    guardianId: string,
    relationshipType: string,
): Promise<GuardianStudentLink> {
    const url = getAPIUrl() + ApiRoutes.SCHOOL_USER + '/' + studentId + '/guardians/' + guardianId;
    const response = await axiosClient.patch<GuardianStudentLink>(url, {
        relationship_type: relationshipType,
    });
    return response.data;
}

export async function unlinkGuardianFromStudent(
    studentId: string,
    guardianId: string
): Promise<void> {
    const url = getAPIUrl() + ApiRoutes.SCHOOL_USER + '/' + studentId + '/guardians/' + guardianId;
    await axiosClient.delete(url);
}
